import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CommunityRulesPanel from '../components/CommunityRulesPanel';
import { getSchemeUrl } from '../utils/deepLinkConfig';

export default function AboutPage() {
  const location = useLocation();

  const { backHref, openAppHref } = useMemo(() => {
    const search = new URLSearchParams(location.search || '');
    const returnTo = search.get('return_to') || '/';
    const ref = search.get('ref');
    const refSrc = search.get('ref_src');
    const refId = search.get('ref_id');

    const nextParams = new URLSearchParams();
    if (ref) nextParams.set('ref', ref);
    if (refSrc) nextParams.set('ref_src', refSrc);
    if (refId) nextParams.set('ref_id', refId);

    let normalizedReturnTo = '/';
    try {
      normalizedReturnTo = decodeURIComponent(returnTo) || '/';
    } catch {
      normalizedReturnTo = returnTo || '/';
    }

    const backUrl = new URL(normalizedReturnTo, 'https://www.gweffit.com');
    nextParams.forEach((value, key) => {
      if (!backUrl.searchParams.get(key)) {
        backUrl.searchParams.set(key, value);
      }
    });

    const routeMatch = backUrl.pathname.match(/^\/t\/([^/]+)/);
    const schemeUrl = routeMatch?.[1]
      ? getSchemeUrl('trial', routeMatch[1], {
          ref: backUrl.searchParams.get('ref'),
          ref_src: backUrl.searchParams.get('ref_src'),
          ref_id: backUrl.searchParams.get('ref_id'),
        })
      : null;

    return {
      backHref: `${backUrl.pathname}${backUrl.search}`,
      openAppHref: schemeUrl || `${backUrl.pathname}${backUrl.search}`,
    };
  }, [location.search]);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-black">How GweffIT Works</h1>
          <Link
            to={backHref}
            className="inline-flex items-center rounded-xl bg-slate-800 border border-slate-700 px-3 py-2 text-sm font-semibold hover:bg-slate-700"
          >
            Back
          </Link>
        </div>

        <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-r from-slate-800 to-slate-900 p-5">
          <p className="text-sm text-slate-200">
            GweffIT turns everyday arguments into fast jury verdicts. Create a trial, share it with your crew, and let people vote to settle it.
          </p>
          <p className="mt-3 text-sm text-slate-300">
            You can jump in as a juror in seconds, follow verdict drama, and use crews to get real opinions from the right people.
          </p>

          <div className="mt-5 space-y-3">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3">
              <div className="font-bold text-white">1. Create</div>
              <div className="text-xs text-slate-400 mt-1">Post a debate or “decide for me” trial with two sides.</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3">
              <div className="font-bold text-white">2. Summon</div>
              <div className="text-xs text-slate-400 mt-1">Share to friends/crews and fill the jury.</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3">
              <div className="font-bold text-white">3. Verdict</div>
              <div className="text-xs text-slate-400 mt-1">Watch the verdict land and see who the jury sided with.</div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <a href={openAppHref} className="rounded-lg bg-purple-600 hover:bg-purple-500 px-4 py-2 text-sm font-bold text-white">
              Open App
            </a>
            <a
              href="https://testflight.apple.com/"
              target="_blank"
              rel="noreferrer"
              className="rounded-lg bg-slate-700 hover:bg-slate-600 px-4 py-2 text-sm font-bold text-slate-100"
            >
              Join TestFlight
            </a>
          </div>

          <div className="mt-5">
            <CommunityRulesPanel title="Community Rules" />
          </div>
        </div>
      </div>
    </div>
  );
}
